import { useContext, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Settings } from 'luxon';
import { z } from 'zod';
import { JxtSmartSlotSelectorContainer } from '@jooxter/ui';
import { ResourceTypeDtoMetaTypeEnum } from '@jooxter/api';
import {
  fromISO,
  getMinBetweenNextHourAndEndOfDay,
  getStandardTime,
  IBookingForm,
  IQuickTimeSlot,
  isPastDateMinusFiveMinutes,
  now,
  toISO,
} from '@jooxter/utils';
import { AutoSelectFirstSlotContext } from '../../../../hooks/bookings/AutoSelectFirstSlotContext';
import { useSlotGenerator } from '../../hooks/useSlotGenerator';
import { isOutOfSync } from '../../hooks/helpers';
import { IJxtBookingQuickSlotsField } from './types';

/** The quick slots the user can pick instead of typing hours, fetched only while this field is mounted. */
export const JxtBookingQuickSlotsField = ({
  schema,
  resourceConfiguration,
  bookingToEdit,
}: IJxtBookingQuickSlotsField) => {
  const autoSelectFirstSlot = useContext<boolean>(AutoSelectFirstSlotContext);
  const { control, setValue } = useFormContext<IBookingForm>();
  const watchResource = useWatch({ control, name: 'resource' });
  const watchStart = useWatch({ control, name: 'start' });
  const watchEnd = useWatch({ control, name: 'end' });
  const watchPhysicalAttendees = useWatch({ control, name: 'physicalAttendees' });
  const resourceTimezone = watchResource?.timezone ? watchResource.timezone : Settings.defaultZone.name;
  const isoStartDate = toISO(watchStart.date);
  const isoEndDate = toISO(watchEnd.date);

  const range = useMemo(() => {
    if (!watchStart || !watchEnd || !resourceTimezone) {
      return;
    }

    if (bookingToEdit) {
      return { from: watchStart.date, to: watchEnd.date };
    }

    // A range that already started cannot be offered as-is: fall back to "now".
    if (isPastDateMinusFiveMinutes(watchStart.date, resourceTimezone)) {
      return { from: now(resourceTimezone), to: getMinBetweenNextHourAndEndOfDay(now(resourceTimezone)) };
    }

    return { from: watchStart.date, to: watchEnd.date };
  }, [watchStart, watchEnd, resourceTimezone, bookingToEdit]);

  const initialRange = useMemo(() => {
    const startAndEndAreDefaults =
      schema?.shape.start instanceof z.ZodDefault && schema?.shape.end instanceof z.ZodDefault;

    if (!startAndEndAreDefaults) {
      return { from: now(resourceTimezone), to: getMinBetweenNextHourAndEndOfDay(now(resourceTimezone)) };
    }

    return {
      from: fromISO(schema.shape.start._def.defaultValue(), resourceTimezone),
      to: fromISO(schema.shape.end._def.defaultValue(), resourceTimezone),
    };
  }, [schema?.shape.start, schema?.shape.end, resourceTimezone]);

  const { slots, isLoading: isSlotLoading } = useSlotGenerator({
    isoStartDate,
    isoEndDate,
    resourceId: watchResource.id,
    defaultSlots: resourceConfiguration?.slots,
    timezone: watchResource.timezone,
    seatRequested: watchPhysicalAttendees,
    bookingMask: bookingToEdit?.id,
    slotType: resourceConfiguration?.slots?.type,
  });

  const setStartEndFromSlot = (slot: IQuickTimeSlot) => {
    setValue(
      'start',
      { date: slot.start, time: getStandardTime(slot.start) },
      { shouldTouch: true, shouldDirty: true, shouldValidate: false }
    );
    setValue(
      'end',
      { date: slot.end, time: getStandardTime(slot.end) },
      { shouldTouch: true, shouldDirty: true, shouldValidate: true }
    );
  };

  return (
    <div className="flex items-center gap-3">
      <div className="size-[25px]" />
      <JxtSmartSlotSelectorContainer
        range={range || initialRange}
        slots={slots}
        loading={
          isSlotLoading ||
          isOutOfSync(
            slots,
            range || initialRange,
            resourceConfiguration?.metaType === ResourceTypeDtoMetaTypeEnum.Vehicle
          )
        }
        onSlotSelection={setStartEndFromSlot}
        // Never auto-select when editing (including the overlap-resolution "move to new resource"
        // pivot, which also mounts with a real `bookingToEdit`): JxtSmartSlotSelectorContainer has
        // no `key` of its own, so it remounts - and re-runs its own auto-select-on-mount effect -
        // whenever this form's outer container remounts, which would otherwise silently overwrite
        // an already-correct start/end with the first available slot.
        autoSelectFirstSlot={autoSelectFirstSlot && !bookingToEdit}
      />
    </div>
  );
};

export default JxtBookingQuickSlotsField;
